import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DataTable from "react-data-table-component";
import { http } from "../helper";
import { BaseUrl } from "../common/config";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import Popup from "../common/popup";
import { dateformatwithuktime } from "../utilityfunctions";

const customStyles = {
  rows: {
    style: {
      minHeight: "25px",
    },
  },
  headCells: {
    style: {
      padding: "8px",
    },
  },
  cells: {
    style: {
      padding: "8px",
    },
  },
};

const paginationComponentOptions = {
  rowsPerPageText: "",
  rangeSeparatorText: "",
  selectAllRowsItem: true,
  selectAllRowsItemText: "",
  noRowsPerPage: "",
};

export const CustomerLifeTimeValue = () => {
  const { t } = useTranslation();

  const getCurrentFiscalYear = useCallback(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    const startYear = month >= 9 ? year : year - 1;
    return `${startYear}-${startYear + 1}`;
  }, []);

  const getCurrentFiscalQuarter = useCallback(() => {
    const month = new Date().getMonth() + 1;
    if (month >= 9 && month <= 11) return 1;
    if (month === 12 || month <= 2) return 2;
    if (month >= 3 && month <= 5) return 3;
    return 4;
  }, []);

  const [filter, setFilter] = useState(0);
  const [topOrderedCustomers, setTopOrderedCustomers] = useState([]);
  const [searchAccount, setSearchAccount] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [period, setPeriod] = useState(getCurrentFiscalYear());
  const [months, setMonths] = useState("9");
  const [quarter, setQuarter] = useState(String(getCurrentFiscalQuarter()));
  const [popupLogView, setPopupLogView] = useState(false);
  const [accountLogs, setAccountLogs] = useState([]);
  const [accountCode, setAccountCode] = useState("");
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);

  const priceFormat = useCallback((price) => {
    return price.toLocaleString("en-US", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 2,
    });
  }, []);

  const formatDate = useCallback((dateValue) => {
    if (!dateValue) return "";
    const dateObject = new Date(dateValue);
    if (Number.isNaN(dateObject.getTime())) return "";
    const year = dateObject.getFullYear();
    const month = String(dateObject.getMonth() + 1).padStart(2, "0");
    const day = String(dateObject.getDate()).padStart(2, "0");
    return `${day}/${month}/${year}`;
  }, []);

  const getAdjustedOnboardedDate = useCallback(
    (account) => {
      if (!account?.accountCreatedAt) return "";
      const originalDate = new Date(account.accountCreatedAt);
      const comparisonDate = new Date("2024-09-02");
      const chosenDate =
        originalDate < comparisonDate ? comparisonDate : originalDate;
      return formatDate(chosenDate);
    },
    [formatDate],
  );

  const getAdjustedActivatedDate = useCallback(
    (account) => {
      if (!account) return "";
      const originalDate = account.recentActivatedDate
        ? new Date(account.recentActivatedDate)
        : account.accountCreatedAt
          ? new Date(account.accountCreatedAt)
          : null;
      if (!originalDate) return "";
      const comparisonDate = new Date("2024-09-02");
      const chosenDate =
        originalDate < comparisonDate ? comparisonDate : originalDate;
      return formatDate(chosenDate);
    },
    [formatDate],
  );

  const MonthNames = useMemo(
    () => [
      t("months.sep"),
      t("months.oct"),
      t("months.nov"),
      t("months.dec"),
      t("months.jan"),
      t("months.feb"),
      t("months.mar"),
      t("months.apr"),
      t("months.may"),
      t("months.jun"),
      t("months.jul"),
      t("months.aug"),
    ],
    [t],
  );

  const quarterNames = useMemo(
    () => ({
      1: t("quarter_sep_nov"),
      2: t("quarter_dec_feb"),
      3: t("quarter_mar_may"),
      4: t("quarter_jun_aug"),
    }),
    [t],
  );

  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const currentMonth = useMemo(() => new Date().getMonth() + 1, []);
  const currentFiscalYear = useMemo(
    () => getCurrentFiscalYear(),
    [getCurrentFiscalYear],
  );

  const quarterOptions = useMemo(() => {
    const currentQuarter = getCurrentFiscalQuarter();
    return Object.entries(quarterNames)
      .filter(([quarterNumber]) => {
        if (period === currentFiscalYear) {
          return Number(quarterNumber) <= currentQuarter;
        }
        return true;
      })
      .map(([quarterNumber, label]) => (
        <option key={quarterNumber} value={quarterNumber}>
          {label}
        </option>
      ));
  }, [period, currentFiscalYear, quarterNames, getCurrentFiscalQuarter]);

  const visibleMonthNames = useMemo(() => {
    if (period !== currentFiscalYear) return MonthNames;
    const fiscalIndex = (new Date().getMonth() - 8 + 12) % 12;
    return MonthNames.slice(0, fiscalIndex + 1);
  }, [period, currentFiscalYear, MonthNames]);

  const yearOptions = useMemo(() => {
    const items = [];
    for (let i = currentYear; i >= 2023; i -= 1) {
      if (i === currentYear && currentMonth < 9) {
        continue;
      }
      items.push(
        <option key={`${i}-${i + 1}`} value={`${i}-${i + 1}`}>
          {`${i}-${i + 1}`}
        </option>,
      );
    }
    return items;
  }, [currentYear, currentMonth]);

  const getTimestamp = useCallback((value) => {
    if (!value) return -Infinity;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? -Infinity : date.getTime();
  }, []);

  const handleActivatedDateClick = useCallback((code) => {
    setAccountCode(code);
    setIsLoadingLogs(true);
    http
      .get(`${BaseUrl}admin/accountlog/${code}`)
      .then((response) => {
        setAccountLogs(response.data.data);
        setIsLoadingLogs(false);
        setPopupLogView(true);
      })
      .catch(() => {
        setAccountLogs([]);
        setIsLoadingLogs(false);
        setPopupLogView(true);
      });
  }, []);

  const columns = useMemo(
    () => [
      {
        name: t("account_code_txt"),
        selector: (row) => row.accountCode,
        sortable: false,
        cell: (row) => (
          <div title={row.accountName || ""}>{row.accountCode}</div>
        ),
      },
      {
        name: t("date_onboarded_txt"),
        selector: (row) => row.accountCreatedAt,
        sortable: true,
        sortFunction: (a, b) =>
          getTimestamp(a.accountCreatedAt) - getTimestamp(b.accountCreatedAt),
        cell: (row) => getAdjustedOnboardedDate(row),
      },
      {
        name: t("activated_date_before_first_order_txt"),
        selector: (row) => row.recentActivatedDate || row.accountCreatedAt,
        sortable: true,
        cell: (row) => {
          const displayDate = getAdjustedActivatedDate(row);
          if (!displayDate) return "";
          return (
            <a
              className="button"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleActivatedDateClick(row.accountCode);
              }}
            >
              {displayDate}
            </a>
          );
        },
      },
      {
        name: t("first_order_date_txt"),
        selector: (row) => row.firstOrderDate,
        sortable: true,
        sortFunction: (a, b) =>
          getTimestamp(a.firstOrderDate) - getTimestamp(b.firstOrderDate),
        cell: (row) => {
          if (!row.firstOrderDate) return "";
          const originalDate = new Date(row.firstOrderDate);
          const comparisonDate = new Date("2024-09-02");
          const displayDate =
            originalDate < comparisonDate ? comparisonDate : originalDate;
          return formatDate(displayDate);
        },
      },
      {
        name: t("recent_order_date_txt"),
        selector: (row) => row.lastOrderDate,
        sortable: true,
        sortFunction: (a, b) =>
          getTimestamp(a.lastOrderDate) - getTimestamp(b.lastOrderDate),
        cell: (row) => {
          if (!row.lastOrderDate) return "";
          const originalDate = new Date(row.lastOrderDate);
          const comparisonDate = new Date("2024-09-02");
          const displayDate =
            originalDate < comparisonDate ? comparisonDate : originalDate;
          return formatDate(displayDate);
        },
      },
      {
        name: t("total_no_of_orders_txt"),
        selector: (row) => row.totalOrders,
        sortable: true,
      },
      {
        name: t("total_ordered_amount_txt"),
        selector: (row) => priceFormat(row.totalValue),
        sortable: true,
        sortFunction: (a, b) => {
          const totalA = parseFloat(a.totalValue) || 0;
          const totalB = parseFloat(b.totalValue) || 0;
          return totalA - totalB;
        },
      },
      {
        name: t("avg_order_interval_txt"),
        selector: (row) => row.avgOrderInterval,
        sortable: true,
      },
      {
        name: t("since_onboarded_in_days_txt"),
        selector: (row) => row.daysActive,
        sortable: true,
      },
      {
        name: t("avg_spent_txt"),
        selector: (row) => priceFormat(row.totalValue / row.totalOrders),
        sortable: true,
        sortFunction: (a, b) => {
          const avgA = parseFloat(a.totalValue / a.totalOrders) || 0;
          const avgB = parseFloat(b.totalValue / b.totalOrders) || 0;
          return avgA - avgB;
        },
      },
    ],
    [t,getTimestamp,getAdjustedOnboardedDate,getAdjustedActivatedDate,handleActivatedDateClick,priceFormat,formatDate]
  );

  const filterLabel = useMemo(() => {
    if (filter === "custom") {
      return fromDate && toDate
        ? `(${formatDate(fromDate)} - ${formatDate(toDate)})`
        : `(${t("all_txt")})`;
    }

    const numFilter = parseInt(filter, 10);
    const valueLabel =
      filter === 0 || numFilter === 0
        ? t("all_txt")
        : filter === "weekly"
          ? t("current_week_txt")
          : filter === "monthly"
            ? t("monthly_txt")
            : filter === "quarterly"
              ? t("quarterly_txt")
              : filter === "FYdata"
                ? t("fy_data_txt")
                : numFilter === 30
                  ? t("past_30_days_txt")
                  : numFilter === 90
                    ? t("past_90_days_txt")
                    : numFilter === 180
                      ? t("past_180_days_txt")
                      : numFilter === 365
                        ? t("past_1_year_txt")
                        : "";

    return [
      t("current_week_txt"),
      t("monthly_txt"),
      t("quarterly_txt"),
      t("fy_data_txt"),
    ].includes(valueLabel)
      ? ""
      : valueLabel
        ? `(${valueLabel})`
        : `(${t("all_txt")})`;
  }, [filter, fromDate, toDate, formatDate, t]);

  const displayedCustomers = useMemo(() => {
    const searchText = searchAccount.trim().toLowerCase();
    if (!searchText) return topOrderedCustomers;
    return topOrderedCustomers.filter((item) =>
      (item.accountCode || "").toString().toLowerCase().includes(searchText),
    );
  }, [searchAccount, topOrderedCustomers]);

  useEffect(() => {
    if (filter === "custom" && (!fromDate || !toDate)) return;
    setIsLoading(true);
    const payload =
      filter === "custom"
        ? {
            filter,
            datefrom: format(new Date(fromDate), "yyyy-MM-dd"),
            dateto: format(new Date(toDate), "yyyy-MM-dd"),
          }
        : ["weekly", "monthly", "quarterly", "FYdata"].includes(filter)
          ? {
              filter,
              datefrom: "",
              dateto: "",
              period,
            }
          : {
              filter: parseInt(filter, 10),
              datefrom: "",
              dateto: "",
            };

    if (filter === "custom") {
      setShowDatePicker(false);
    }
    http
      .post(`${BaseUrl}admin/customerlifetimevalue`, payload)
      .then((response) => {
        const data = response.data.data || [];
        const normalizedData = data.map((item) => {
          if (item.accountCreatedAt) {
            const originalDate = new Date(item.accountCreatedAt);
            const comparisonDate = new Date("2024-09-02");
            if (originalDate < comparisonDate) {
              return {
                ...item,
                accountCreatedAt: comparisonDate.toISOString(),
              };
            }
          }
          return item;
        });
        const actualMonth = parseInt(months, 10);
        const filteredData =
          filter === "monthly"
            ? normalizedData.filter((item) => item.month === actualMonth)
            : filter === "quarterly"
              ? normalizedData.filter(
                  (item) => item.fiscalQuarter === parseInt(quarter, 10),
                )
              : normalizedData;

        setTopOrderedCustomers(filteredData);
      })
      .catch(() => {
        setTopOrderedCustomers([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [filter, fromDate, toDate, months, quarter, period]);

  const changeFilterHandler = useCallback((e) => {
    const value = e.target.value;
    setFilter(value);
    if (value === "custom") {
      setShowDatePicker(true);
    } else {
      setShowDatePicker(false);
      setFromDate(null);
      setToDate(null);
    }
  }, []);

  const changePeriodHandler = useCallback(
    (e) => {
      const newPeriod = e.target.value;
      setPeriod(newPeriod);
      const isCurrentFiscalYear = newPeriod === currentFiscalYear;
      if (filter === "monthly") {
        const currentMonthIndex = new Date().getMonth();
        const monthsPassedSinceFiscalStart = (currentMonthIndex - 8 + 12) % 12;
        const maxVisibleIndex = monthsPassedSinceFiscalStart;
        const visibleMonthNames = isCurrentFiscalYear
          ? MonthNames.slice(0, maxVisibleIndex + 1)
          : MonthNames;

        const selectedMonthIndex = MonthNames.indexOf(months);
        const isValidMonth =
          selectedMonthIndex >= 0 &&
          selectedMonthIndex < visibleMonthNames.length;
        if (!isValidMonth) {
          setMonths("9");
        }
      }
      if (filter === "quarterly") {
        const currentQuarter = getCurrentFiscalQuarter();
        const maxQuarter = isCurrentFiscalYear ? currentQuarter : 4;
        if (parseInt(quarter, 10) > maxQuarter) {
          setQuarter(String(maxQuarter || 1));
        }
      }
    },
    [currentFiscalYear,filter,MonthNames,months,quarter,getCurrentFiscalQuarter]
  );

  const changeMonthHandler = useCallback((e) => {
    setMonths(e.target.value);
  }, []);

  const DateChange = useCallback(
    (value, type) => {
      if (type === "from") {
        setFromDate(value);
        if (toDate && value && value > toDate) {
          setToDate(null);
        }
      } else if (type === "to") {
        if (!fromDate || value >= fromDate) {
          setToDate(value);
        } else {
          setToDate(null);
        }
      }
    },
    [fromDate, toDate],
  );

  const handleChange = useCallback((e) => {
    setSearchAccount(e.target.value);
  }, []);

  let loadingbuffer = isLoading ? "loading-buffer" : "";

  return (
    <div
      className={`chart-container chart-customers-life-time ${loadingbuffer}`}
      style={{ verticalAlign: "top" }}
    >
      <div className="chart-Hdr">
        <div className="chart-Hdr-txt">
          <h2>{t("customer_lifetime_value_txt")}</h2>
        </div>
        <div className="chart-fltr-Hldr">
          <div
            className="chart-fltr"
            style={{ width: "190px" }}
          >
            <input
              type="text"
              value={searchAccount}
              onChange={handleChange}
              placeholder={t("search_by_account_code_txt")}
            />
          </div>
          <div className="chart-fltr" />
          <div
            className="chart-fltr"
            onMouseEnter={() => {
              if (filter === "custom" && !showDatePicker) {
                setShowDatePicker(true);
              }
            }}
            onMouseLeave={() => {
              if (filter === "custom" && showDatePicker) {
                setShowDatePicker(false);
              }
            }}
          >
            <select value={filter} onChange={changeFilterHandler}>
              <option value={0}>{t("all_txt")}</option>
              <option value="weekly">{t("current_week_txt")}</option>
              <option value="monthly">{t("monthly_txt")}</option>
              <option value="quarterly">{t("quarterly_txt")}</option>
              <option value="FYdata">{t("fy_data_txt")}</option>
              <option value={30}>{t("past_30_days_txt")}</option>
              <option value={90}>{t("past_90_days_txt")}</option>
              <option value={180}>{t("past_180_days_txt")}</option>
              <option value={365}>{t("past_1_year_txt")}</option>
              <option value="custom">{t("custom_date_txt")}</option>
            </select>
            {showDatePicker && (
              <div className="date-picker-container">
                <label>{t("from_txt")}:</label>
                <DatePicker
                  selected={fromDate}
                  onChange={(date) => DateChange(date, "from")}
                  showDisabledMonthNavigation
                  dateFormat="dd/MM/yyyy"
                  disablePast={true}
                  maxDate={new Date()}
                  placeholderText={t("select_date_txt")}
                />
                <label>{t("to_txt")}:</label>
                <DatePicker
                  selected={toDate}
                  onChange={(date) => DateChange(date, "to")}
                  showDisabledMonthNavigation
                  dateFormat="dd/MM/yyyy"
                  disablePast={true}
                  maxDate={new Date()}
                  placeholderText={t("select_date_txt")}
                />
              </div>
            )}
            {filter === "quarterly" && (
              <select
                style={{ width: "160px" }}
                value={quarter}
                onChange={(e) => setQuarter(String(e.target.value))}
              >
                {quarterOptions}
              </select>
            )}
            {filter === "monthly" && (
              <select
                style={{ width: "50px" }}
                value={months}
                onChange={changeMonthHandler}
              >
                {visibleMonthNames.map((monthName, index) => {
                  const monthNumber = ((index + 8) % 12) + 1;
                  return (
                    <option key={monthNumber} value={monthNumber}>
                      {monthName}
                    </option>
                  );
                })}
              </select>
            )}
            {(filter === "monthly" ||
              filter === "quarterly" ||
              filter === "FYdata") && (
              <select
                style={{ width: "170px" }}
                value={period}
                onChange={changePeriodHandler}
              >
                {yearOptions}
              </select>
            )}
          </div>
        </div>
        <div className="fltr-sub-val">
          {displayedCustomers.length > 0 && (
            <span className="fltr-val">
              {t("total_records", { count: displayedCustomers.length })}
              {filterLabel ? ` ${filterLabel}` : ""}
            </span>
          )}
        </div>
      </div>
      <DataTable
        columns={columns}
        data={displayedCustomers}
        customStyles={customStyles}
        noDataComponent={
          <div className="no-records-found">
            {t("no_data_filtered_search_txt")}
          </div>
        }
        pagination
        paginationPerPage={10}
        paginationRowsPerPageOptions={[10, 20, 40, 60, 80, 100]}
        paginationComponentOptions={paginationComponentOptions}
      />
      {popupLogView && (
        <Popup
          extClass="info-poup show"
          closebtn={
            <span className="close" onClick={() => setPopupLogView(false)} />
          }
          headMoreTxt={
            <div className="order-top">
              <h4>
                {t("customer_status_log_details_txt")} - {accountCode}
              </h4>
            </div>
          }
          bodyMoreTxt={
            <div className="bottom vendor-details acc-log">
              {isLoadingLogs ? (
                <div>{t("loading_txt")}</div>
              ) : (
                <>
                  {accountLogs &&
                    accountLogs.length > 0 &&
                    accountLogs.map((log, index) => {
                      const [action, notes] = log.noteText.split(/-(.+)/);
                      const status =
                        action.trim() === "activate"
                          ? t("activated_txt")
                          : t("deactivated_txt");

                      return (
                        <div key={index} className="info-item">
                          <div className="info-innr">
                            <div className="flex-log-hdr">
                              <p>
                                <span className="labeltxt acn-by">
                                  {status}
                                </span>
                              </p>
                              <div className="hdrlog-rit">
                                {log.createdBy && (
                                  <>
                                    <span className="log-user">
                                      {log.createdBy}
                                    </span>
                                    <span className="log-line">|</span>
                                  </>
                                )}
                                <span className="log-date">
                                  {dateformatwithuktime(log.createdAt)}
                                </span>
                              </div>
                            </div>
                            <p>
                              <span className="labeltxt log-reason">
                                {t("reason_txt")}:{" "}
                              </span>
                              {notes ? notes.trim() : ""}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  {(() => {
                    const account = displayedCustomers.find(
                      (acc) => acc.accountCode === accountCode,
                    );
                    if (!account) return null;
                    const onboarded = getAdjustedOnboardedDate(account);
                    const activated = getAdjustedActivatedDate(account);
                    return (
                      <div className="info-item">
                        <div className="info-innr">
                          <p>
                            {onboarded === activated
                              ? `${t("onboarded_activated_on_txt")} ${onboarded}`
                              : `${t("onboarded_activated_on_txt")} (${onboarded} & ${activated})`}
                          </p>
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          }
        />
      )}
    </div>
  );
};
